import { useMemo, useState } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { getWeeklyActivitySnapshot } from '../utils/studentProgressInsights'
import './WeeklyActivityStrip.css'

const CHART_POINTS_LIMIT = 30

function WeeklyActivityStrip({ attempts }) {
  const [chartOpen, setChartOpen] = useState(false)
  const snapshot = useMemo(() => getWeeklyActivitySnapshot(attempts || []), [attempts])

  const chartData = useMemo(() => {
    if (!attempts || attempts.length < 2) return []
    const sorted = [...attempts].sort((a, b) => {
      const dateA = a.completedAt?.toDate ? a.completedAt.toDate() : new Date(a.completedAt || 0)
      const dateB = b.completedAt?.toDate ? b.completedAt.toDate() : new Date(b.completedAt || 0)
      return dateA - dateB
    })
    return sorted.slice(-CHART_POINTS_LIMIT).map((a, i) => {
      const date = a.completedAt?.toDate ? a.completedAt.toDate() : new Date(a.completedAt || 0)
      return {
        name: `Ex. ${i + 1}`,
        score: a.score ?? 0,
        date: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      }
    })
  }, [attempts])

  const formatDelta = (direction, pct, suffix) => {
    if (!snapshot.hasLastWeek) return 'vs sem. dernière : —'
    if (direction === 'stable' || pct === 0) return `vs sem. dernière : ≈ (${suffix})`
    const sign = direction === 'up' ? '+' : '−'
    return `vs sem. dernière : ${sign}${pct}${suffix.includes('%') ? '' : ''} ${suffix}`
  }

  if (!attempts || attempts.length === 0 || snapshot.uniqueCount === 0) {
    return (
      <div className="weekly-activity-strip">
        <p className="weekly-activity-eyebrow">Cette semaine</p>
        <p className="weekly-activity-empty">Aucune activité cette semaine.</p>
      </div>
    )
  }

  return (
    <div className="weekly-activity-strip">
      <p className="weekly-activity-eyebrow">Cette semaine</p>
      <div className="weekly-activity-grid">
        <div className="weekly-activity-item">
          <span className="weekly-activity-value">{snapshot.uniqueCount}</span>
          <span className="weekly-activity-label">Exercices uniques</span>
          <span
            className={`weekly-activity-delta weekly-activity-delta--${snapshot.deltaCountDirection}`}
          >
            {formatDelta(snapshot.deltaCountDirection, snapshot.deltaCountPct, '% vol.')}
          </span>
        </div>
        {snapshot.sessionCount > snapshot.uniqueCount ? (
          <div className="weekly-activity-item">
            <span className="weekly-activity-value">{snapshot.sessionCount}</span>
            <span className="weekly-activity-label">Sessions</span>
          </div>
        ) : null}
        <div className="weekly-activity-item">
          <span className="weekly-activity-value">
            {snapshot.avgScore != null ? `${snapshot.avgScore}%` : '—'}
          </span>
          <span className="weekly-activity-label">Score moyen (records)</span>
          <span
            className={`weekly-activity-delta weekly-activity-delta--${snapshot.deltaScoreDirection}`}
          >
            {formatDelta(snapshot.deltaScoreDirection, snapshot.deltaScorePct, 'pts')}
          </span>
        </div>
        <div className="weekly-activity-item">
          <span className="weekly-activity-value">{snapshot.activeDays}/7</span>
          <span className="weekly-activity-label">Jours actifs</span>
        </div>
        <div className="weekly-activity-item">
          <span className="weekly-activity-value">
            {snapshot.bestScore != null ? `${snapshot.bestScore}%` : '—'}
          </span>
          <span className="weekly-activity-label">Meilleur</span>
        </div>
      </div>

      {chartData.length >= 2 ? (
        <>
          <button
            type="button"
            className="weekly-activity-chart-toggle"
            onClick={() => setChartOpen((o) => !o)}
            aria-expanded={chartOpen}
          >
            {chartOpen ? 'Masquer l’évolution' : 'Voir l’évolution des scores'}
          </button>
          {chartOpen ? (
            <div className="weekly-activity-chart-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="suiviScoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#667eea" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#667eea" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                    width={36}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const d = payload[0].payload
                      return (
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '0.5rem 0.75rem', borderRadius: 8 }}>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{d.date}</div>
                          <div style={{ fontWeight: 700 }}>{d.score}%</div>
                        </div>
                      )
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="#667eea"
                    strokeWidth={2}
                    fill="url(#suiviScoreGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default WeeklyActivityStrip
